import { useEffect, useState } from "react";
import { visibleViewportVars } from "@/theme";

/** How much of the app's own viewport is hidden, in CSS pixels. */
export type VisibleInsets = {
  /** Hidden strip above the visible band. */
  top: number;
  /** Hidden strip below the visible band. */
  bottom: number;
  /** Height of the visible band. */
  height: number;
};

const FULLY_VISIBLE: VisibleInsets = { top: 0, bottom: 0, height: 0 };

/** Sub-pixel slack when comparing the frame's width with the browser window. */
const SOLE_SCROLLER_TOLERANCE = 4;

/**
 * Whether the app is the only thing on screen that can scroll its content.
 *
 * True when the frame spans the full width of the browser window, which means
 * no part of the host is beside it and none can be above or below it either:
 * the host's fullscreen control. There is then no host scrollbar to bring a
 * clipped bottom into view, so the app must size itself to what is on screen
 * rather than to its content.
 *
 * Width, specifically. The obvious test is whether the visible band covers the
 * window, but the band is clipped by the frame's own height, so once the app
 * asks for a frame shorter than the window the band can never reach it again
 * and the app stays small forever. Width is set by the host's layout and the
 * app never requests it, so it cannot be self-limiting. It is also synchronous:
 * a fullscreen toggle changes the frame's width, which fires `resize` here with
 * the new width already readable, while the band arrives later through an
 * `IntersectionObserver` — measuring against the band therefore raced the
 * transition and left the frame at the wrong size until something else happened
 * to trigger another measurement.
 *
 * `screen.height` cannot answer this — it describes the display, not who can
 * scroll — and `window.screenY` reports the browser window's position on screen
 * and never moves inside a frame.
 */
export const isSoleScroller = (view: Window): boolean =>
  view.innerWidth + SOLE_SCROLLER_TOLERANCE >= view.outerWidth;

/*
 * Distance between the markers whose crossings tell us the band moved. A host
 * scroll of less than this can go unnoticed for a frame, which is why it is
 * small enough to be invisible in a dialog's centring but large enough to keep
 * the marker count near 60 for a tall frame.
 */
const MARKER_SPACING = 16;

/**
 * Turns one IntersectionObserver reading of a viewport-sized element into the
 * hidden strips above and below it.
 *
 * Both rects are in the app's own client coordinates, so subtracting them gives
 * the insets directly. Returns `null` for a reading that says nothing useful —
 * a frame scrolled entirely out of view reports an empty rect, and publishing
 * a zero-height visible band on the way past would throw overlay layout away.
 */
export const visibleInsetsFrom = (
  bounding: { top: number; bottom: number; height: number },
  intersection: { top: number; bottom: number; height: number },
): VisibleInsets | null => {
  if (intersection.height <= 0 || bounding.height <= 0) return null;

  return {
    top: Math.max(0, Math.round(intersection.top - bounding.top)),
    bottom: Math.max(0, Math.round(bounding.bottom - intersection.bottom)),
    height: Math.round(intersection.height),
  };
};

/**
 * Measures how much of the app's viewport is on screen and publishes it as the
 * `visibleViewportVars` custom properties.
 *
 * The app reports its naturally growing content height to the host. Loop Hubs
 * writes that pixel height to both the `<iframe>` and its wrapper while the
 * surrounding host scroll container adds 112px of its own padding, so the frame
 * can be taller than the area it shows. Everything that positions itself
 * against the iframe viewport can then aim at pixels nobody can see:
 * dialogs centre below the fold, dropdowns open into the hidden strip, and a
 * toast pinned to the top of the frame lands behind the host's header once the
 * host is scrolled.
 *
 * `IntersectionObserver` is the one measurement that survives the frame
 * boundary: its intersection rect is clipped by every ancestor frame's viewport,
 * cross-origin included, which is the property ad viewability was built on.
 * Verified against a replica of the hub's geometry (now `scripts/hubs/`) in a
 * genuinely cross-origin frame: a 1061px frame showing 780px reported exactly
 * the 281px bottom inset the host measured, and the split moved to the top inset
 * as the host scrolled.
 *
 * Two observers, because one cannot do both jobs:
 *
 * - A viewport-sized sentinel gives exact insets, but only when a reading is
 *   delivered. Thresholds are no help — as a host scrolls, the band slides
 *   without changing size, so the ratio barely moves (0.735 to 0.743 over 200px
 *   in the replica) and a 1% threshold ladder fires roughly once per 200px.
 * - A ladder of thin markers down the viewport is the trigger. One of them
 *   crosses a band edge within `MARKER_SPACING` of scrolling, and each crossing
 *   schedules a fresh one-shot observation of the sentinel, whose first delivery
 *   is the current geometry.
 *
 * Everything reports zero when the app owns its viewport, so nothing downstream
 * changes when the app runs standalone or in a host that sizes the frame
 * correctly. A ResizeObserver on the measuring layer also handles hosts that
 * resize the iframe without dispatching a useful window resize to the child.
 */
export const useVisibleViewport = (): VisibleInsets => {
  const [insets, setInsets] = useState<VisibleInsets>(FULLY_VISIBLE);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const root = document.documentElement;

    /*
     * A host control can overlap our frame, and we cannot see it to measure it:
     * the hub paints its expand button 8px from the frame's top-right corner and
     * the parent document always paints above frame content, so it covers the
     * corner our toast anchors to. 48px clears that button and is inert while
     * the app is not embedded.
     */
    if (window.self !== window.top) {
      root.style.setProperty(visibleViewportVars.hostCorner, "48px");
    }

    // Fixed and inset, so it is the app's viewport whatever the document does.
    const layer = document.createElement("div");
    layer.setAttribute("aria-hidden", "true");
    layer.style.cssText =
      "position:fixed;inset:0;pointer-events:none;opacity:0;z-index:-1;";

    const sentinel = document.createElement("div");
    sentinel.style.cssText = "position:absolute;inset:0;";
    layer.appendChild(sentinel);
    document.body.appendChild(layer);

    let ladder: IntersectionObserver | null = null;
    let viewportResizeObserver: ResizeObserver | null = null;
    let pendingMeasure = false;
    let measuring = false;
    let disposed = false;

    const publish = (next: VisibleInsets) => {
      root.style.setProperty(visibleViewportVars.top, `${next.top}px`);
      root.style.setProperty(visibleViewportVars.bottom, `${next.bottom}px`);
      root.style.setProperty(visibleViewportVars.height, `${next.height}px`);

      /*
       * Scroll room is only the app's job when nothing else can do it. While a
       * host page is on screen around the frame it owns the scrolling, and
       * adding room here just produces a second scrollbar running off the
       * bottom of the window into blank space.
       */
      const scrollRoom = isSoleScroller(window) ? next.bottom : 0;
      root.style.setProperty(visibleViewportVars.scrollRoom, `${scrollRoom}px`);

      setInsets(previous =>
        previous.top === next.top &&
        previous.bottom === next.bottom &&
        previous.height === next.height
          ? previous
          : next,
      );
    };

    /*
     * A newly created observer always delivers the current geometry on its first
     * callback, which is the only way to read the clipped rect on demand —
     * `takeRecords` on a long-lived observer returns what has been queued, not a
     * fresh measurement.
     */
    const measure = () => {
      if (disposed) return;
      if (measuring) {
        pendingMeasure = true;
        return;
      }
      measuring = true;

      const once = new IntersectionObserver(entries => {
        once.disconnect();
        measuring = false;

        const entry = entries[entries.length - 1];
        if (entry) {
          const next = visibleInsetsFrom(
            entry.boundingClientRect,
            entry.intersectionRect,
          );
          if (next) publish(next);
        }

        if (pendingMeasure) {
          pendingMeasure = false;
          measure();
        }
      });
      once.observe(sentinel);
    };

    /*
     * Rebuilt on resize: the marker count follows the viewport height, and a
     * host that resizes the frame is also the case where the insets change most.
     */
    const buildLadder = () => {
      ladder?.disconnect();
      layer
        .querySelectorAll("[data-marker]")
        .forEach(marker => marker.remove());

      const height = layer.clientHeight;
      ladder = new IntersectionObserver(measure);

      for (let offset = 0; offset <= height; offset += MARKER_SPACING) {
        const marker = document.createElement("div");
        marker.dataset.marker = "";
        marker.style.cssText = `position:absolute;left:0;width:100%;height:1px;top:${offset}px;`;
        layer.appendChild(marker);
        ladder.observe(marker);
      }

      measure();
    };

    buildLadder();
    if (typeof ResizeObserver !== "undefined") {
      viewportResizeObserver = new ResizeObserver(buildLadder);
      viewportResizeObserver.observe(layer);
    } else {
      window.addEventListener("resize", buildLadder);
    }

    return () => {
      disposed = true;
      viewportResizeObserver?.disconnect();
      if (!viewportResizeObserver) {
        window.removeEventListener("resize", buildLadder);
      }
      ladder?.disconnect();
      layer.remove();
      root.style.removeProperty(visibleViewportVars.top);
      root.style.removeProperty(visibleViewportVars.bottom);
      root.style.removeProperty(visibleViewportVars.height);
      root.style.removeProperty(visibleViewportVars.hostCorner);
      root.style.removeProperty(visibleViewportVars.scrollRoom);
    };
  }, []);

  return insets;
};
